/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import {
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    Dialog,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    DialogType,
    ICommandBarItemProps
} from "@fluentui/react";
import styles from "./RulesGrid.module.css";
import { RulesGridCommands } from "./RulesGridViewModels";
import { IRulesGridState } from "./IRulesGridState";
import { IRulesGridProps } from "./IRulesGridProps";
import * as Panels from "../Panels/RulePanel/RulePanel";
import { IRulePanelData } from "../Panels/RulePanel/IRulePanelData";
import { IFieldUI, ISettingGroupDefinition } from "@nuance/hierarchical-settings-ui-domain";
import { useBoolean } from "@fluentui/react-hooks";
import Grid from "../../Grid/Grid";
import { IGridItem } from "../../Grid/GridModels";

const rulesGridCommands = new RulesGridCommands();
const cmds = rulesGridCommands.Cmds;

const getAppliedToCsv = (rule: IGridItem, definition: ISettingGroupDefinition): string => {
    const appliedTo = definition.UI.Controls?.map(c => {
        const ui = c as IFieldUI;

        if (ui.VisibleRuleName === rule.id) {
            return `${ui.SettingName} (hidden)`;
        }

        if (ui.EnableRuleName === rule.id) {
            return `${ui.SettingName} (disabled)`;
        }
    })?.filter(c => c !== undefined);

    return appliedTo?.join(", ");
};

const RulesGrid = (props: IRulesGridProps): JSX.Element => {
    const ruleTableColumns: IColumn[] = [
        {
            key: "nameCol",
            name: "Rule name",
            fieldName: "id",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            className: styles.ruleName
        },
        {
            key: "targetSetting",
            name: "Target Setting",
            fieldName: "SettingName",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true
        },
        {
            key: "regex",
            name: "Regex",
            fieldName: "RegexTemplate",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true
        },
        {
            key: "appliedTo",
            name: "Applied To",
            fieldName: "appliedTo",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IGridItem) => <>{getAppliedToCsv(item, props.definition)}</>
        }
    ];

    const initialState: IRulesGridState = {
        currentRuleIndex: -1,
        isRulePanelOpen: false,
        definition: props.definition
    };

    const [state, dispatch] = React.useReducer(rulesGridCommands.Reducer, initialState);

    React.useEffect(() => {
        props.advancedViewVM.dispatch(props.advancedViewVM.cmds.definitionChange(state.definition));
    }, [state.definition]);

    const onClosePanel = () => {
        dispatch(cmds.onCancelRulePanel());
    };

    const onSavePanel = (data: IRulePanelData) => {
        dispatch(cmds.onSaveRulePanel(data));
    };

    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);

    const dialogContentProps = {
        type: DialogType.normal,
        title: "Delete rule?",
        subText: `Are you sure you want to delete '${
            state.definition.UI.Rules?.at(state.currentRuleIndex)?.RuleName
        }'?`
    };

    const onDeleteConfirm = () => {
        // delete the thing
        dispatch(cmds.onDeleteRule());
        // close the dialog
        toggleHideDialog();
    };

    const _defaultButtonDefinitions: ICommandBarItemProps[] = [
        {
            key: "add",
            text: "Add",
            iconProps: { iconName: "Add" },
            onClick: () => dispatch(cmds.onAddRule())
        }
    ];

    const _onSelectedButtonDefinitions: ICommandBarItemProps[] = [
        ..._defaultButtonDefinitions,
        {
            key: "edit",
            text: "Edit",
            iconProps: { iconName: "Edit" },
            onClick: () => dispatch(cmds.onEditRule())
        },
        {
            key: "delete",
            text: "Delete",
            iconProps: { iconName: "Delete" },
            onClick: toggleHideDialog
        }
    ];

    const items = React.useMemo(
        () =>
            state.definition.UI.Rules.slice(0).map((rule, index) => {
                const { RuleName, ...rest } = rule;
                return { id: RuleName, index, ...rest };
            }),
        [state.definition.UI.Rules]
    );

    const actions = {
        left: state.currentRuleIndex >= 0 ? _onSelectedButtonDefinitions : _defaultButtonDefinitions
    };

    return (
        <>
            <Grid
                id="ruleName"
                columns={ruleTableColumns}
                items={items ?? []}
                layout={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.single}
                actions={actions}
                onSelectRow={selectedRows => dispatch(cmds.onRuleSelectionChanged(selectedRows[0]))}
            />

            {state.currentRulePanelProps?.data && (
                <Panels.RulePanel
                    onClosePanel={onClosePanel}
                    onSaveContent={onSavePanel}
                    {...state.currentRulePanelProps}
                />
            )}

            <Dialog
                hidden={hideDialog}
                onDismiss={toggleHideDialog}
                dialogContentProps={dialogContentProps}
                modalProps={{ isBlocking: true }}
                maxWidth={560}
                minWidth={560}
            >
                <DialogFooter>
                    <PrimaryButton onClick={onDeleteConfirm} text="Yes, delete" />
                    <DefaultButton onClick={toggleHideDialog} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default RulesGrid;
